import axios from 'axios';

// Fonction pour obtenir les headers d'authentification
const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    };
};

// Configuration de l'API URL
const getApiUrl = () => {
    const baseUrl = process.env.REACT_APP_BASEURL_CLIENT_BACKEND || "https://localhost:4000/";
    return baseUrl.replace(/\/+$/, '') + '/';
};
const API_URL = getApiUrl();

// Debug: Log de l'URL API
console.log('BeneficiaryService API_URL:', API_URL);

class BeneficiaryService {
    // Liste paginée des bénéficiaires avec profils complets
    async getBeneficiariesList(filters = {}) {
        try {
            const params = new URLSearchParams();
            Object.keys(filters).forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });
            
            const url = API_URL + "BeneficiaryProfile/list?" + params.toString();
            const headers = getAuthHeaders();
            
            console.log('BeneficiaryService - Making request to:', url);
            console.log('BeneficiaryService - Headers:', headers);
            console.log('BeneficiaryService - Filters:', filters);
            
            const response = await axios.get(url, { headers });
            
            console.log('BeneficiaryService - Response status:', response.status);
            console.log('BeneficiaryService - Response data:', response.data);
            
            // Retourner directement la réponse de l'API - elle est déjà dans le bon format
            return response.data;
            
        } catch (error) {
            console.error('BeneficiaryService - API Error:', error);
            
            // En production, ne pas retourner de données de test
            console.error('BeneficiaryService - API Error in production, no fallback data');
            
            throw error;
        }
    }

    // Mappe le nouveau format BeneficiaryProfileResponse vers le format Client attendu
    mapProfileToClient(profile) {
        if (!profile) return null;

        return {
            id: profile.PersonalInfo?.Id || profile.PersonalInfo?.id,
            matricule: profile.PersonalInfo?.NumeroBeneficiaire || profile.PersonalInfo?.matricule,
            numeroAssure: profile.PersonalInfo?.NumeroAssure || profile.PersonalInfo?.numeroAssure,
            firstName: profile.PersonalInfo?.FirstName || profile.PersonalInfo?.firstName,
            lastName: profile.PersonalInfo?.LastName || profile.PersonalInfo?.lastName,
            phoneNumber: profile.PersonalInfo?.PhoneNumber || profile.PersonalInfo?.phoneNumber,
            email: profile.PersonalInfo?.Email || profile.PersonalInfo?.email,
            sexe: profile.PersonalInfo?.Gender || profile.PersonalInfo?.sexe,
            birthdate: profile.PersonalInfo?.Birthdate || profile.PersonalInfo?.birthdate,
            status: this.calculateDisplayStatus(profile),
            city: profile.PersonalInfo?.City || profile.PersonalInfo?.city,
            numeroPolice: profile.PersonalInfo?.NumeroPolice || profile.PersonalInfo?.numeroPolice,
            photoPath: profile.Photos?.length > 0 ? profile.Photos[0].PhotoPath : null,
            
            // Données enrichies du profil complet
            contract: profile.Contract,
            restrictions: profile.ActiveRestrictions || [],
            insuranceCards: profile.InsuranceCards || [],
            documents: profile.Documents || [],
            photos: profile.Photos || [],
            healthParameters: profile.HealthParameters,
            identificationHistory: profile.IdentificationHistory || [],
            profileStatus: profile.Status,
            metadata: profile.Metadata
        };
    }

    // Calcule le statut d'affichage basé sur le profil complet
    calculateDisplayStatus(profile) {
        if (!profile.Status) return 'UNKNOWN';
        
        if (!profile.Status.IsActive) {
            return 'INACTIVE';
        }
        
        if (profile.Status.HasActiveRestrictions) {
            return 'RESTRICTED';
        }
        
        if (!profile.Status.IsComplete) {
            return 'INCOMPLETE';
        }
        
        return 'ACTIVE';
    }


    // Profil complet d'un bénéficiaire par ID
    async getBeneficiaryDetails(beneficiaryId) {
        try {
            const response = await axios.get(
                API_URL + `BeneficiaryProfile/${beneficiaryId}`,
                { headers: getAuthHeaders() }
            );
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération du profil:', error);
            throw error;
        }
    }

    // Profil complet d'un bénéficiaire par numéro d'assuré
    async getBeneficiaryProfileByNumber(numeroAssure) {
        try {
            const response = await axios.get(
                API_URL + `BeneficiaryProfile/by-number/${numeroAssure}`,
                { headers: getAuthHeaders() }
            );
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération du profil par numéro:', error);
            throw error;
        }
    }

    // Profil complet avec données contractuelles enrichies
    async getBeneficiaryProfileWithContract(numeroAssure) {
        try {
            const response = await axios.get(
                API_URL + `BeneficiaryProfile/with-contract/${numeroAssure}`,
                { headers: getAuthHeaders() }
            );
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération du profil enrichi:', error);
            throw error;
        }
    }

    // Restrictions d'un bénéficiaire (hiérarchiques)
    async getBeneficiaryRestrictions(beneficiaryId) {
        try {
            // Utiliser le nouveau profil complet pour obtenir toutes les restrictions hiérarchiques
            const profile = await this.getBeneficiaryDetails(beneficiaryId);
            return {
                success: true,
                data: profile?.restrictions || [],
                message: `${profile?.restrictions?.length || 0} restriction(s) trouvée(s) (tous niveaux confondus)`
            };
        } catch (error) {
            console.error('Erreur lors de la récupération des restrictions:', error);
            
            // Fallback vers l'ancienne API
            try {
                const response = await axios.get(
                    API_URL + `client/${beneficiaryId}/restrictions`,
                    { headers: getAuthHeaders() }
                );
                return response.data;
            } catch (fallbackError) {
                console.error('Fallback API aussi en erreur:', fallbackError);
                throw error;
            }
        }
    }

    // Documents d'un bénéficiaire
    async getBeneficiaryDocuments(beneficiaryId) {
        try {
            // Utiliser le nouveau profil complet pour obtenir les documents
            const profile = await this.getBeneficiaryDetails(beneficiaryId);
            return {
                success: true,
                data: profile?.documents || [],
                message: `${profile?.documents?.length || 0} document(s) trouvé(s)`
            };
        } catch (error) {
            console.error('Erreur lors de la récupération des documents:', error);
            
            // Fallback vers l'ancienne API
            try {
                const response = await axios.get(
                    API_URL + `client/${beneficiaryId}/documents`,
                    { headers: getAuthHeaders() }
                );
                return response.data;
            } catch (fallbackError) {
                console.error('Fallback API aussi en erreur:', fallbackError);
                throw error;
            }
        }
    }

    // Carte d'assurance d'un bénéficiaire
    async getBeneficiaryCard(beneficiaryId) {
        try {
            // Utiliser le nouveau profil complet pour obtenir les cartes
            const profile = await this.getBeneficiaryDetails(beneficiaryId);
            return {
                success: true,
                data: profile?.insuranceCards || [],
                message: `${profile?.insuranceCards?.length || 0} carte(s) trouvée(s)`
            };
        } catch (error) {
            console.error('Erreur lors de la récupération des cartes:', error);
            
            // Fallback vers l'ancienne API
            try {
                const response = await axios.get(
                    API_URL + `client/${beneficiaryId}/card`,
                    { headers: getAuthHeaders() }
                );
                return response.data;
            } catch (fallbackError) {
                console.error('Fallback API aussi en erreur:', fallbackError);
                throw error;
            }
        }
    }

    // Historique d'un bénéficiaire
    async getBeneficiaryHistory(beneficiaryId) {
        try {
            // Utiliser le nouveau profil complet pour obtenir l'historique
            const profile = await this.getBeneficiaryDetails(beneficiaryId);
            return {
                success: true,
                data: profile?.identificationHistory || [],
                message: `${profile?.identificationHistory?.length || 0} entrée(s) d'historique trouvée(s)`
            };
        } catch (error) {
            console.error('Erreur lors de la récupération de l\'historique:', error);
            
            // Fallback vers l'ancienne API
            try {
                const response = await axios.get(
                    API_URL + `client/${beneficiaryId}/history`,
                    { headers: getAuthHeaders() }
                );
                return response.data;
            } catch (fallbackError) {
                console.error('Fallback API aussi en erreur:', fallbackError);
                throw error;
            }
        }
    }

    // Imprimer la carte d'un bénéficiaire
    async printBeneficiaryCard(beneficiaryId, options = {}) {
        const response = await axios.post(
            API_URL + `client/${beneficiaryId}/card/print`,
            options,
            { 
                headers: getAuthHeaders(),
                responseType: 'blob' // Pour les PDFs
            }
        );
        return response.data;
    }

    // Recherche de bénéficiaires
    async searchBeneficiaries(searchTerm, filters = {}) {
        const params = new URLSearchParams();
        params.append('search', searchTerm);
        
        Object.keys(filters).forEach(key => {
            if (filters[key]) {
                params.append(key, filters[key]);
            }
        });
        
        const response = await axios.get(
            API_URL + "client/search?" + params.toString(),
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    // Mise à jour des informations d'un bénéficiaire
    async updateBeneficiary(beneficiaryId, data) {
        const response = await axios.put(
            API_URL + `client/${beneficiaryId}`,
            data,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    // Upload de document
    async uploadDocument(beneficiaryId, documentData) {
        const formData = new FormData();
        formData.append('file', documentData.file);
        formData.append('documentType', documentData.type);
        formData.append('description', documentData.description || '');
        
        const response = await axios.post(
            API_URL + `client/${beneficiaryId}/documents`,
            formData,
            { 
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'multipart/form-data'
                }
            }
        );
        return response.data;
    }

    // Statistiques des bénéficiaires
    async getBeneficiariesStats(filters = {}) {
        const params = new URLSearchParams();
        Object.keys(filters).forEach(key => {
            if (filters[key]) {
                params.append(key, filters[key]);
            }
        });
        
        const response = await axios.get(
            API_URL + "client/stats?" + params.toString(),
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    // Export des bénéficiaires
    async exportBeneficiaries(filters = {}, format = 'excel') {
        const params = new URLSearchParams();
        Object.keys(filters).forEach(key => {
            if (filters[key]) {
                params.append(key, filters[key]);
            }
        });
        params.append('format', format);
        
        const response = await axios.get(
            API_URL + "client/export?" + params.toString(),
            { 
                headers: getAuthHeaders(),
                responseType: 'blob'
            }
        );
        return response.data;
    }

    // === NOUVELLES MÉTHODES POUR LES ENDPOINTS HIÉRARCHIQUES ===

    // Récupère toutes les restrictions hiérarchiques d'un bénéficiaire par son numéro
    async getAllRestrictionsForBeneficiary(numeroAssure) {
        try {
            const response = await axios.get(
                API_URL + `BeneficiaryProfile/${numeroAssure}/restrictions`,
                { headers: getAuthHeaders() }
            );
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération des restrictions hiérarchiques:', error);
            throw error;
        }
    }

    // Vérifie si un bénéficiaire a des restrictions actives
    async checkActiveRestrictions(numeroAssure) {
        try {
            const response = await axios.get(
                API_URL + `BeneficiaryProfile/check-restrictions/${numeroAssure}`,
                { headers: getAuthHeaders() }
            );
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la vérification des restrictions:', error);
            throw error;
        }
    }

    // Récupère les photos avec informations biométriques
    async getBeneficiaryPhotos(beneficiaryId) {
        try {
            const profile = await this.getBeneficiaryDetails(beneficiaryId);
            return {
                success: true,
                data: profile?.photos || [],
                message: `${profile?.photos?.length || 0} photo(s) trouvée(s)`
            };
        } catch (error) {
            console.error('Erreur lors de la récupération des photos:', error);
            throw error;
        }
    }

    // Diagnostic de l'API
    async getDiagnostic() {
        try {
            const response = await axios.get(
                API_URL + `BeneficiaryProfile/diagnostic`,
                { headers: getAuthHeaders() }
            );
            return response.data;
        } catch (error) {
            console.error('Erreur lors du diagnostic:', error);
            throw error;
        }
    }

    // Test de santé de l'API
    async getHealthCheck() {
        try {
            const response = await axios.get(
                API_URL + `BeneficiaryProfile/health`,
                { headers: getAuthHeaders() }
            );
            return response.data;
        } catch (error) {
            console.error('Erreur lors du health check:', error);
            throw error;
        }
    }
}

export default new BeneficiaryService();